import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useLanguage } from "../context/LanguageContext";
import api from "../services/api";

function AdminSettings() {
  const { language, t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [settings, setSettings] = useState({
    maxTickets: 3,
    timeWindowHours: 1,
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get("/admin-settings");
      setSettings({
        maxTickets: response.data.maxTickets,
        timeWindowHours: response.data.timeWindowHours,
      });
      setError("");
    } catch (err) {
      setError(err.response?.data?.message || t("settings.loadError"));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError("");
      setSuccess("");

      // Validate
      if (settings.maxTickets < 0) {
        setError(t("settings.invalidMaxTickets"));
        setSaving(false);
        return;
      }

      if (settings.timeWindowHours < 0.1) {
        setError(t("settings.invalidTimeWindow"));
        setSaving(false);
        return;
      }

      const response = await api.put("/admin-settings", settings);
      setSuccess(response.data.message || t("settings.saveSuccess"));

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.message || t("settings.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  if (loading) {
    return (
      <Container
        maxWidth="md"
        sx={{ mt: 4, display: "flex", justifyContent: "center" }}
      >
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          {t("settings.title")}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t("settings.description")}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess("")}
          >
            {success}
          </Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <TextField
            label={t("settings.maxTicketsLabel")}
            type="number"
            value={settings.maxTickets}
            onChange={(e) =>
              handleChange("maxTickets", parseInt(e.target.value) || 0)
            }
            helperText={t("settings.maxTicketsHelp")}
            fullWidth
            inputProps={{ min: 0, step: 1 }}
          />

          <TextField
            label={t("settings.timeWindowLabel")}
            type="number"
            value={settings.timeWindowHours}
            onChange={(e) =>
              handleChange("timeWindowHours", parseFloat(e.target.value) || 0.1)
            }
            helperText={t("settings.timeWindowHelp")}
            fullWidth
            inputProps={{ min: 0.1, step: 0.5 }}
          />

          <Box
            sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 2 }}
          >
            <Button
              variant="outlined"
              onClick={fetchSettings}
              disabled={saving}
            >
              {t("settings.reset")}
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? <CircularProgress size={24} /> : t("settings.save")}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
}

export default AdminSettings;
